import { Button, Popconfirm } from 'antd'
import { UnresolvedCommentBadge } from 'features/libra/comments/UnresolvedCommentBadge'
import { StagePill } from 'features/libra/components/LifecyclePills'
import {
  getLifecycleStage,
  isTerminalStage,
} from 'features/libra/lifecycle/lifecycleStageHelpers'
import type { LifecycleConfig } from 'features/libra/lifecycle/lifecycleTypes'
import type { LibraTicket } from 'features/libra/ticket/libraTicketsApi'
import { getVersionHistory } from 'features/libra/ticket/ticketVersioning'
import { TicketProgressBar } from 'features/libra/ticketMenu/TicketProgressBar'
import type { LibraWorkflow } from 'features/libra/workflow/workflowStepsApi'
import type { FC, MouseEvent } from 'react'
import { useMemo, useState } from 'react'
import styled from 'styled-components'
import type { ThemeType } from 'styles/GeckoTheme'
import { displayDate } from 'utils/dateHelpers'
import { parseDate } from 'utils/parsingHelpers'
import { displayNameFromEmail } from 'utils/stringHelpers'

function getCompletedByLabel(completedBy: string | null): string | undefined {
  return completedBy ?? undefined
}

const formatLocale = new Intl.DateTimeFormat().resolvedOptions().locale

function formatFinalizedDate(
  updatedAt: string | undefined
): string | undefined {
  const date = parseDate(updatedAt)
  if (!date) return undefined
  return displayDate(date, formatLocale)
}

export const TicketAccordionItem: FC<{
  ticket: LibraTicket
  allTickets: ReadonlyArray<LibraTicket>
  workflow: LibraWorkflow | undefined
  onTicketClick: (ticket: LibraTicket) => void
  onCreateVersion: (ticket: LibraTicket) => void
  onDeleteLatestVersion: (ticket: LibraTicket) => Promise<void>
  isDeletingLatestVersion: boolean
  canCreateVersion?: boolean
  unresolvedCount?: number
}> = ({
  ticket,
  allTickets,
  workflow,
  onTicketClick,
  onCreateVersion,
  onDeleteLatestVersion,
  isDeletingLatestVersion,
  canCreateVersion = true,
  unresolvedCount,
}) => {
  const [showHistory, setShowHistory] = useState(false)
  const versionHistory = useMemo(
    () => getVersionHistory(allTickets, ticket.assessmentId),
    [allTickets, ticket.assessmentId]
  )
  const olderVersions = useMemo(
    () => versionHistory.filter((version) => version.id !== ticket.id),
    [ticket.id, versionHistory]
  )
  const hasPreviousCompleteVersion = useMemo(
    () => olderVersions.some((version) => version.workflowComplete),
    [olderVersions]
  )
  const completedBy = getCompletedByLabel(ticket.completedBy)
  const isComplete = ticket.workflowComplete

  const handleOpen = (e: MouseEvent) => {
    e.stopPropagation()
    onTicketClick(ticket)
  }

  const handleCreateVersion = (e: MouseEvent) => {
    e.stopPropagation()
    onCreateVersion(ticket)
  }

  const handleToggleHistory = (e: MouseEvent) => {
    e.stopPropagation()
    setShowHistory((prev) => !prev)
  }

  const handleDeleteLatestButtonClick = (e: MouseEvent) => {
    e.stopPropagation()
  }

  const handleDeleteConfirm = (e?: MouseEvent<HTMLElement>) => {
    e?.preventDefault()
    e?.stopPropagation()
    void onDeleteLatestVersion(ticket)
  }

  const handleDeleteCancel = (e?: MouseEvent<HTMLElement>) => {
    e?.preventDefault()
    e?.stopPropagation()
  }

  return (
    <TicketRow>
      <TicketCard>
        <TicketHeader>
          <TicketContent>
            <TicketTitle>{ticket.title}</TicketTitle>
            {workflow && <WorkflowName>{workflow.name}</WorkflowName>}
            <MetaRow>
              <Pill $variant='version'>v{ticket.version}</Pill>
              <LifecycleStagePill
                ticket={ticket}
                lifecycleConfig={workflow?.lifecycleConfig}
                isComplete={isComplete}
              />
              <UnresolvedCommentBadge count={unresolvedCount} />
              <LifecycleMetaText
                ticket={ticket}
                lifecycleConfig={workflow?.lifecycleConfig}
                isComplete={isComplete}
                completedBy={completedBy}
              />
              {ticket.createdBy && (
                <MetaText>
                  Created by {displayNameFromEmail(ticket.createdBy)}
                </MetaText>
              )}
            </MetaRow>
          </TicketContent>
          {workflow && (
            <ProgressContainer>
              <TicketProgressBar ticket={ticket} workflow={workflow} circle />
            </ProgressContainer>
          )}
        </TicketHeader>
        <ActionRow>
          <Button type='primary' size='small' onClick={handleOpen}>
            Open
          </Button>
          {isTerminalStage(ticket, workflow?.lifecycleConfig) &&
            canCreateVersion && (
              <NewVersionButton
                type='dashed'
                size='small'
                onClick={handleCreateVersion}>
                New Version
              </NewVersionButton>
            )}
          {!isComplete && hasPreviousCompleteVersion && (
            <Popconfirm
              title='Delete latest version?'
              description='This removes the current in-progress version. Older versions will remain.'
              okText='Delete'
              cancelText='Cancel'
              okButtonProps={{ danger: true, loading: isDeletingLatestVersion }}
              onConfirm={handleDeleteConfirm}
              onCancel={handleDeleteCancel}>
              <DeleteVersionButton
                size='small'
                loading={isDeletingLatestVersion}
                onClick={handleDeleteLatestButtonClick}>
                Delete Latest
              </DeleteVersionButton>
            </Popconfirm>
          )}
          {olderVersions.length > 0 && (
            <HistoryToggleButton
              size='small'
              type='text'
              onClick={handleToggleHistory}>
              {showHistory
                ? 'Hide history'
                : `View ${olderVersions.length} older version${olderVersions.length > 1 ? 's' : ''}`}
            </HistoryToggleButton>
          )}
        </ActionRow>
        {showHistory && olderVersions.length > 0 && (
          <VersionHistoryList>
            <HistoryTitle>Version history</HistoryTitle>
            {olderVersions.map((versionTicket) => {
              const historyCompletedBy = getCompletedByLabel(
                versionTicket.completedBy
              )
              const historyComplete = versionTicket.workflowComplete

              return (
                <HistoryItem key={versionTicket.id}>
                  <HistoryMeta>
                    <Pill $variant='version'>v{versionTicket.version}</Pill>
                    <Pill $variant={historyComplete ? 'complete' : 'progress'}>
                      {historyComplete ? 'Completed' : 'In Progress'}
                    </Pill>
                    {historyComplete ? (
                      <>
                        {historyCompletedBy && (
                          <MetaText>by {historyCompletedBy}</MetaText>
                        )}
                        {formatFinalizedDate(versionTicket.updatedAt) && (
                          <MetaText>
                            {historyCompletedBy ? '· ' : ''}
                            {formatFinalizedDate(versionTicket.updatedAt)}
                          </MetaText>
                        )}
                      </>
                    ) : (
                      <MetaText>still in progress</MetaText>
                    )}
                  </HistoryMeta>
                  <Button
                    size='small'
                    type='default'
                    onClick={(e) => {
                      e.stopPropagation()
                      onTicketClick(versionTicket)
                    }}>
                    Open
                  </Button>
                </HistoryItem>
              )
            })}
          </VersionHistoryList>
        )}
      </TicketCard>
    </TicketRow>
  )
}

export const TicketAccordionContent: FC<{
  tickets: ReadonlyArray<LibraTicket>
  allTickets: ReadonlyArray<LibraTicket>
  workflowMap: ReadonlyMap<string, LibraWorkflow>
  onTicketClick: (ticket: LibraTicket) => void
  onCreateVersion: (ticket: LibraTicket) => void
  onDeleteLatestVersion: (ticket: LibraTicket) => Promise<void>
  deletingTicketId?: string
  canCreateVersion?: (ticket: LibraTicket) => boolean
  unresolvedCounts?: ReadonlyMap<string, number>
}> = ({
  tickets,
  allTickets,
  workflowMap,
  onTicketClick,
  onCreateVersion,
  onDeleteLatestVersion,
  deletingTicketId,
  canCreateVersion,
  unresolvedCounts,
}) => {
  const renderItem = (ticket: LibraTicket) => (
    <TicketAccordionItem
      key={ticket.id}
      ticket={ticket}
      allTickets={allTickets}
      workflow={workflowMap.get(ticket.workflowId)}
      onTicketClick={onTicketClick}
      onCreateVersion={onCreateVersion}
      onDeleteLatestVersion={onDeleteLatestVersion}
      isDeletingLatestVersion={deletingTicketId === ticket.id}
      canCreateVersion={canCreateVersion?.(ticket) ?? true}
      unresolvedCount={unresolvedCounts?.get(ticket.id)}
    />
  )

  return <TicketsContainer>{tickets.map(renderItem)}</TicketsContainer>
}

const LifecycleStagePill: FC<{
  ticket: LibraTicket
  lifecycleConfig?: LifecycleConfig
  isComplete: boolean
}> = ({ ticket, lifecycleConfig, isComplete }) => {
  const stage = getLifecycleStage(ticket, lifecycleConfig)
  if (stage) {
    return <StagePill $color={stage.color}>{stage.name}</StagePill>
  }
  return (
    <Pill $variant={isComplete ? 'complete' : 'progress'}>
      {isComplete ? 'Completed' : 'In Progress'}
    </Pill>
  )
}

const LifecycleMetaText: FC<{
  ticket: LibraTicket
  lifecycleConfig?: LifecycleConfig
  isComplete: boolean
  completedBy?: string
}> = ({ ticket, lifecycleConfig, isComplete, completedBy }) => {
  const stage = getLifecycleStage(ticket, lifecycleConfig)
  if (stage?.terminal) {
    const transitionDate = ticket.terminalTransitionAt
      ? new Date(ticket.terminalTransitionAt).toLocaleDateString()
      : ''
    return (
      <>
        <MetaText>{stage.name}</MetaText>
        {ticket.terminalTransitionBy && (
          <MetaText>
            by {ticket.terminalTransitionBy}
            {transitionDate ? ` · ${transitionDate}` : ''}
          </MetaText>
        )}
      </>
    )
  }
  return (
    <>
      {isComplete && completedBy && <MetaText>by {completedBy}</MetaText>}
      {isComplete && formatFinalizedDate(ticket.updatedAt) && (
        <MetaText>
          {completedBy ? '· ' : ''}
          {formatFinalizedDate(ticket.updatedAt)}
        </MetaText>
      )}
    </>
  )
}

const TicketCard = styled.div`
  padding: 0.75rem 0.85rem;
  border: 1px solid ${({ theme }) => theme.colors.gray[700]};
  background: ${({ theme }) => theme.colors.gray[800]};
  border-radius: 8px;
  cursor: default;
  transition: border-color 0.2s ease;
  width: 100%;
  overflow: hidden;
`

const TicketsContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.35rem;
  overflow: visible;
  padding-bottom: 0.55rem;
`

const TicketRow = styled.div`
  overflow: visible;
`

const TicketHeader = styled.div`
  display: flex;
  align-items: flex-start;
  gap: 1rem;
`

const TicketContent = styled.div`
  flex: 1;
  min-width: 0;
`

const TicketTitle = styled.div`
  font-weight: 600;
  color: ${({ theme }) => theme.forecast.activeText};
  margin-bottom: 0.2rem;
  font-size: 0.85rem;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`

const WorkflowName = styled.div`
  font-size: 0.75rem;
  color: ${({ theme }) => theme.forecast.defaultText};
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`

const MetaRow = styled.div`
  display: flex;
  align-items: center;
  gap: 0.35rem;
  margin-top: 0.35rem;
  flex-wrap: wrap;
`

const MetaText = styled.span`
  font-size: 0.75rem;
  color: ${({ theme }) => theme.forecast.secondaryText};
`

type PillVariant = 'version' | 'complete' | 'progress'

const pillColor = (variant: PillVariant, theme: ThemeType): string => {
  switch (variant) {
    case 'version':
      return theme.common.badge.text.blue
    case 'complete':
      return theme.common.badge.text.green
    case 'progress':
    default:
      return theme.common.badge.text.orange
  }
}

const pillBackground = (variant: PillVariant, theme: ThemeType): string => {
  switch (variant) {
    case 'complete':
      return theme.common.badge.background.green
    default:
      return theme.colors.gray[700]
  }
}

const Pill = styled.span<{ $variant: PillVariant }>`
  font-size: 0.68rem;
  line-height: 1;
  font-weight: 600;
  border-radius: 999px;
  padding: 0.2rem 0.45rem;
  color: ${({ $variant, theme }) => pillColor($variant, theme)};
  background: ${({ $variant, theme }) => pillBackground($variant, theme)};
  border: 1px solid ${({ theme }) => theme.colors.gray[600]};
`

const ProgressContainer = styled.div`
  flex-shrink: 0;
`

const ActionRow = styled.div`
  display: flex;
  align-items: center;
  gap: 0.5rem;
  margin-top: 0.65rem;
`

const HistoryToggleButton = styled(Button)`
  && {
    margin-left: auto;
    color: ${({ theme }) => theme.forecast.secondaryText} !important;
  }
`

const VersionHistoryList = styled.div`
  margin-top: 0.85rem;
  border-top: 1px solid ${({ theme }) => theme.colors.gray[700]};
  padding-top: 0.65rem;
  display: flex;
  flex-direction: column;
  gap: 0.45rem;
`

const HistoryTitle = styled.div`
  font-size: 0.72rem;
  font-weight: 600;
  letter-spacing: 0.3px;
  text-transform: uppercase;
  color: ${({ theme }) => theme.forecast.secondaryText};
`

const HistoryItem = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 0.5rem;
  background: ${({ theme }) => theme.colors.gray[800]};
  border: 1px solid ${({ theme }) => theme.colors.gray[700]};
  border-radius: 6px;
  padding: 0.45rem 0.5rem;
`

const HistoryMeta = styled.div`
  display: flex;
  align-items: center;
  gap: 0.5rem;
`

const NewVersionButton = styled(Button)`
  && {
    background: ${({ theme }) => theme.forecast.defaultButton};
    border-color: ${({ theme }) => theme.forecast.border};
    color: ${({ theme }) => theme.forecast.activeText};
    font-weight: 500;
  }

  &&:hover {
    border-color: ${({ theme }) => theme.forecast.primary};
    color: ${({ theme }) => theme.forecast.activeText};
    background: ${({ theme }) => theme.forecast.hoverButton};
  }
`

const DeleteVersionButton = styled(Button)`
  && {
    border-color: ${({ theme }) =>
      theme.forecast.danger ?? theme.common.alert.error};
    background: ${({ theme }) => theme.forecast.defaultButton};
    color: ${({ theme }) => theme.forecast.danger ?? theme.common.alert.error};
    font-weight: 500;
    opacity: 0.8;
  }

  &&:hover {
    border-color: ${({ theme }) =>
      theme.forecast.danger ?? theme.common.alert.error};
    background: ${({ theme }) => theme.forecast.redGlassBackground};
    color: ${({ theme }) => theme.forecast.danger ?? theme.common.alert.error};
    opacity: 1;
  }
`
